import { useCallback, useEffect, useState } from 'react';
import type { FacetCategory } from '../components/SearchAutocomplete';
import { trackSearch, tryTriggerSupportModal } from '../utils/supportModalTracking';
import { useSearchState } from '../views/useSearchState';
import type { RiftboundFilter } from '../types';

interface UseDebouncedUrlSearchTermReturn {
  localTerm: string;
  setLocalTerm: (term: string) => void;
  filter: RiftboundFilter;
  setFilter: (f: RiftboundFilter) => void;
  handleCommitSearch: (term: string) => void;
  handleCommitFacet: (category: FacetCategory, value: string) => void;
}

/**
 * Shared logic for sidebar bound components: debounces local search term to URL
 * and exposes commit helpers for immediate-update autocomplete interactions.
 */
export function useDebouncedUrlSearchTerm(): UseDebouncedUrlSearchTermReturn {
  const { filter, searchTerm, setFilter, setSearchTerm } = useSearchState();
  const [localTerm, setLocalTerm] = useState(searchTerm);

  useEffect(() => {
    setLocalTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (localTerm === searchTerm) return;
    const handle = setTimeout(() => {
      setSearchTerm(localTerm);
      trackSearch(localTerm);
      tryTriggerSupportModal();
    }, 250);
    return () => clearTimeout(handle);
  }, [localTerm, searchTerm, setSearchTerm]);

  const handleCommitSearch = useCallback(
    (term: string) => {
      setLocalTerm(term);
      setSearchTerm(term);
      trackSearch(term);
      tryTriggerSupportModal();
    },
    [setSearchTerm],
  );

  const handleCommitFacet = useCallback(
    (category: FacetCategory, value: string) => {
      const current = (filter[category] as string[]) ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setFilter({ ...filter, [category]: next });
    },
    [filter, setFilter],
  );

  return { localTerm, setLocalTerm, filter, setFilter, handleCommitSearch, handleCommitFacet };
}
